import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FiSave, FiRotateCcw } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import WeeklyScheduleEditor from './weeklyScheduleEditor';
import { SectionCard, PageLoader, ErrorState, AlertBox, preventEnterSubmit } from './common';
import { NumberField, SwitchField } from './fields';
import {
    DAY_STATUS, SETTING_FIELDS, VISIBLE_SETTING_FIELDS, scheduleToRows, rowsToSchedule, validateSchedule, validateSettings,
    toNumberOrNull, toInputValue, getErrorMessage, fieldFromMessage, formatDateTime, hasErrors
} from './helpers';

const toForm = (general) => ({
    maxAdvanceDays: toInputValue(general?.maxAdvanceDays),
    minLeadMinutes: toInputValue(general?.minLeadMinutes),
    allowSameDayBooking: !!general?.allowSameDayBooking,
    slotMinutes: toInputValue(general?.slotMinutes),
    slotMaxSeats: toInputValue(general?.slotMaxSeats),
    rows: scheduleToRows(general?.weeklySchedule, DAY_STATUS.NORMAL)
});

const GeneralTab = ({ general, loading, loadError, onRetry, onSaved }) => {
    const initialForm = useMemo(() => toForm(general), [general]);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [dayErrors, setDayErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(initialForm);
        setErrors({});
        setDayErrors({});
        setServerError('');
    }, [initialForm]);

    const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

    const setField = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const setRows = (rows) => {
        setForm(prev => ({ ...prev, rows }));
        if (hasErrors(dayErrors)) setDayErrors(validateSchedule(rows));
    };

    const handleDiscard = () => {
        setForm(initialForm);
        setErrors({});
        setDayErrors({});
        setServerError('');
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (saving) return;
        const body = {
            maxAdvanceDays: toNumberOrNull(form.maxAdvanceDays),
            minLeadMinutes: toNumberOrNull(form.minLeadMinutes),
            allowSameDayBooking: form.allowSameDayBooking,
            slotMinutes: toNumberOrNull(form.slotMinutes),
            slotMaxSeats: toNumberOrNull(form.slotMaxSeats),
            weeklySchedule: rowsToSchedule(form.rows)
        };
        const fieldErrors = validateSettings(body);
        const scheduleErrors = validateSchedule(form.rows);
        setErrors(fieldErrors);
        setDayErrors(scheduleErrors);
        setServerError('');
        if (hasErrors(fieldErrors) || hasErrors(scheduleErrors)) {
            toastMessage('error', 'Please fix the highlighted fields');
            return;
        }

        setSaving(true);
        slotSettingActions.updateGeneral(body).then(res => {
            setSaving(false);
            toastMessage('success', 'General settings saved');
            onSaved(res);
        }).catch(err => {
            setSaving(false);
            const message = getErrorMessage(err, 'Unable to save general settings');
            const field = fieldFromMessage(message);
            if (field && field !== 'weeklySchedule' && SETTING_FIELDS.some(f => f.key === field)) {
                setErrors({ [field]: message });
            }
            setServerError(message);
            toastMessage('error', message);
        });
    };

    if (loading) return <SectionCard><PageLoader text="Loading general settings..." /></SectionCard>;
    if (loadError) return <SectionCard><ErrorState message={loadError} onRetry={onRetry} /></SectionCard>;

    return (
        <Form onSubmit={handleSave} onKeyDown={preventEnterSubmit} noValidate>
            {serverError && (
                <AlertBox title="Settings were not saved" messages={serverError} onClose={() => setServerError('')} />
            )}

            <SectionCard
                title="Booking rules"
                subtitle="These rules apply to every center unless the center overrides them."
            >
                <Row className="g-3 row-cols-1 row-cols-md-3">
                    {VISIBLE_SETTING_FIELDS.map(field => (
                        <Col key={field.key}>
                            <Form.Label htmlFor={`g-${field.key}`}>{field.label}</Form.Label>
                            <NumberField
                                id={`g-${field.key}`}
                                value={form[field.key]}
                                suffix={field.suffix}
                                min={field.min}
                                max={field.max}
                                placeholder={field.key === 'maxAdvanceDays' ? 'No limit' : ''}
                                error={errors[field.key]}
                                disabled={saving}
                                onChange={value => setField(field.key, value)}
                            />
                            {!errors[field.key] && <span className="ss-hint">{field.hint}</span>}
                        </Col>
                    ))}
                    <Col>
                        <Form.Label>Same-day booking</Form.Label>
                        <div className="ss-toggle-tile">
                            <span>{form.allowSameDayBooking ? 'Allowed' : 'Not allowed'}</span>
                            <SwitchField id="g-allowSameDayBooking" checked={form.allowSameDayBooking} disabled={saving}
                                onChange={checked => setField('allowSameDayBooking', checked)} />
                        </div>
                        <span className="ss-hint">When on, customers can book a slot for today.</span>
                    </Col>
                </Row>
            </SectionCard>

            <SectionCard
                title="Weekly schedule"
                subtitle="Click a day to change it. The strip shows the whole week at a glance; Copy applies one day to other days."
                bodyClassName="p-0"
                footer={(
                    <div className="ss-actions">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {isDirty && <span className="ss-dirty">Unsaved changes</span>}
                            {general?.updatedTime && <span className="ss-meta">Last updated {formatDateTime(general.updatedTime)}</span>}
                        </div>
                        <div className="ss-actions-buttons">
                            <Button variant="light" disabled={!isDirty || saving} onClick={handleDiscard}>
                                <FiRotateCcw /> Discard
                            </Button>
                            <Button variant="primary" type="submit" disabled={!isDirty || saving}>
                                <FiSave /> {saving ? 'Saving...' : 'Save changes'}
                            </Button>
                        </div>
                    </div>
                )}
            >
                <WeeklyScheduleEditor rows={form.rows} onChange={setRows} errors={dayErrors} disabled={saving} />
            </SectionCard>
        </Form>
    );
};

export default GeneralTab;
